import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Order } from "@/hooks/useOrder";
import { Logo } from "../Logo";
import { Button } from "../ui/button";
import SuccessIcon from "./success-icon";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { PendingIcon } from "./pending-icon";
import { RejectedIcon } from "./rejected-icon";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function OrderConfirmation({
  order,
  location,
  goToMenu,
  onRefresh,
  useShortOrderNumber,
}: {
  order: Order;
  location: {
    name: string;
    cvr: string;
    id: string;
  };
  goToMenu: () => void;
  onRefresh?: () => void;
  useShortOrderNumber?: boolean;
}) {
  // Calculate subtotal & tax
  function convertPriceToNumber(price: string) {
    return Number(price.replace(/[^0-9.-]+/g, ""));
  }

  const total = order.transactionAmount;
  const subtotal = convertPriceToNumber(total) * 0.8;
  const tax = convertPriceToNumber(total) - subtotal;

  const ticketStatus = order.ticketStatus || "IN_PROGRESS";

  const scenario = getDeliveryMethodScenario(order);

  // Auto-refresh for pending orders
  useEffect(() => {
    if (["IN_PROGRESS", "PENDING"].includes(ticketStatus) && onRefresh) {
      let loadingToast: string | number | undefined;

      const interval = setInterval(() => {
        // Dismiss previous toast if it exists
        if (loadingToast) {
          toast.dismiss(loadingToast);
        }

        // Show new loading toast
        loadingToast = toast.loading("Checking order status...", {
          position: "bottom-center",
        });

        // Call refresh and dismiss toast after a short delay
        onRefresh();

        setTimeout(() => {
          if (loadingToast) {
            toast.dismiss(loadingToast);
            loadingToast = undefined;
          }
        }, 2000);
      }, 15000); // Refresh every 15 seconds

      return () => {
        clearInterval(interval);
        if (loadingToast) {
          toast.dismiss(loadingToast);
        }
      };
    }
  }, [ticketStatus, onRefresh]);

  // Helper functions for different states
  const getStatusIcon = () => {
    switch (ticketStatus) {
      case "PENDING":
        return <PendingIcon className="mx-auto" />;
      case "REJECTED":
        return <RejectedIcon className="mx-auto" />;
      case "IN_PROGRESS":
        if (["mixed", "counter"].includes(scenario)) {
          return <PendingIcon className="mx-auto" />;
        }

        return <SuccessIcon />;
      default:
        return <SuccessIcon />;
    }
  };

  const getStatusTitle = () => {
    switch (ticketStatus) {
      case "PENDING":
        return "Order Received";
      case "REJECTED":
        return "Order Cancelled";
      case "IN_PROGRESS":
        if (["mixed", "counter"].includes(scenario)) {
          return "Confirmed! \n We are preparing your order";
        }
        return "Order Confirmed!";
      default:
        if (
          ticketStatus === "COMPLETED" &&
          ["mixed", "counter"].includes(scenario)
        ) {
          return "Order Ready for Pickup!";
        }
        return "Order Confirmed!";
    }
  };

  const getStatusColor = () => {
    switch (ticketStatus) {
      case "PENDING":
        return "text-black";
      case "REJECTED":
        return "text-red-600";
      default:
        return "";
    }
  };

  function getOrderNumber(id: string): string {
    if (useShortOrderNumber && id.length <= 3) return `#${id}`;

    if (useShortOrderNumber === true) return `#${id.slice(-3)}`;

    return `#${id}`;
  }

  return (
    <motion.div
      className="max-w-md w-full space-y-6"
      initial={{ opacity: 0, y: "50%" }}
      animate={{ opacity: 1, y: "0%" }}
      transition={{
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1],
        scale: {
          type: "spring",
          damping: 15,
          stiffness: 200,
        },
      }}
    >
      <Card className="w-full bg-background text-foreground">
        <CardHeader className="pb-4 text-center">
          {getStatusIcon()}

          <CardTitle
            className={`text-2xl tracking-tighter font-medium whitespace-pre-wrap ${getStatusColor()}`}
          >
            {useShortOrderNumber && order.oposOrderId && (
              <>
                {getOrderNumber(order.oposOrderId)}
                <br />
              </>
            )}

            {getStatusTitle()}
          </CardTitle>
          <CardDescription className="text-foreground">
            <ConfirmationMessage order={order} ticketStatus={ticketStatus} />
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Order Info */}
          <div className="flex justify-between items-center">
            <div>
              {order.oposOrderId && (
                <p className="text-sm text-foreground/70">
                  Order ID: {order.oposOrderId}
                </p>
              )}

              {order.table && (
                <p className="text-sm text-foreground/70 mb-3">
                  Table: {order.table} ({location.name})
                </p>
              )}
              {order.transactionId && (
                <p className="text-xs text-foreground/70">
                  Payment ID: {order.transactionId ?? ""}
                </p>
              )}

              {order.updatedAt && (
                <p className="text-xs text-foreground/70 mb-2">
                  Time:{" "}
                  {new Date(order.updatedAt).toLocaleDateString("da-DK", {
                    timeZone: "Europe/Copenhagen",
                  })}{" "}
                  {new Date(order.updatedAt).toLocaleTimeString("da-DK", {
                    timeZone: "Europe/Copenhagen",
                  })}
                </p>
              )}
            </div>
          </div>

          <Separator />

          {/* Order Items */}
          <div className="space-y-4">
            <p className="font-medium">Your Order</p>
            <OrderItemsList order={order} ticketStatus={ticketStatus} />
          </div>

          <Separator />

          {/* Order Total */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <p>Subtotal</p>
              <p
                className={cn(
                  ticketStatus === "REJECTED" && "opacity-50 line-through",
                )}
              >
                {subtotal.toFixed(2).replace(".00", "")} DKK
              </p>
            </div>
            <div className="flex justify-between text-sm">
              <p>VAT (25%)</p>
              <p
                className={cn(
                  ticketStatus === "REJECTED" && "opacity-50 line-through",
                )}
              >
                {tax.toFixed(2).replace(".00", "")} DKK
              </p>
            </div>
            <div className="flex justify-between font-medium">
              <p>Total</p>
              <p
                className={cn(
                  ticketStatus === "REJECTED" && "opacity-50 line-through",
                )}
              >
                {total}
              </p>
            </div>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col justify-center pb-6 pt-2">
          <p className="text-sm text-center text-foreground/70">
            {ticketStatus === "PENDING"
              ? "Please wait while we confirm your order with the kitchen."
              : ticketStatus === "REJECTED"
                ? "We apologize for the inconvenience. Please contact us if you have any questions."
                : "Thank you for your order! If you need assistance, please ask a staff member."}
          </p>
          <div className="max-w-[150px] mx-auto flex items-center justify-center">
            <Logo />
          </div>

          <Button variant={"default"} onClick={goToMenu}>
            See menu
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

function getDeliveryMethodScenario(order: Order) {
  const cart = order.clientOrderCart ?? [];
  if (cart.length === 0) return "table" as const;
  const methods = new Set(
    cart.map((i) => i.onPremiseDeliveryMethod).filter(Boolean),
  );
  if (methods.has("TABLE_SERVICE") && methods.has("PICKUP_AT_COUNTER"))
    return "mixed" as const;
  if (methods.size === 1 && methods.has("PICKUP_AT_COUNTER"))
    return "counter" as const;
  return "table" as const;
}

function OrderItemsList({
  order,
  ticketStatus,
}: {
  order: Order;
  ticketStatus: string;
}) {
  const scenario = getDeliveryMethodScenario(order);
  const rejected = ticketStatus === "REJECTED";

  // Products and cart items are matched by option name (e.g. "1/2lb", "1 Portion").
  // Multiple different products can share the same option name, so we use a queue
  // (array per name, consumed with shift()) to pair them in order rather than a
  // simple Map which would overwrite duplicates.
  // We pull `count` from the cart (actual purchase qty) rather than `productQuantity`
  // (which can represent servings, e.g. 5 beers in a pitcher is qty 5 but count 1).
  // TODO: this would be unnecessary if the products array included externalId from
  // the POS — we could then match directly on cart item externalId instead.
  const cartQueueByOptionName = new Map<
    string,
    { count: number; method?: string | null }[]
  >();
  for (const c of order.clientOrderCart ?? []) {
    if (!cartQueueByOptionName.has(c.name))
      cartQueueByOptionName.set(c.name, []);
    cartQueueByOptionName.get(c.name)!.push({
      count: c.count,
      method: c.onPremiseDeliveryMethod,
    });
  }

  const products = (order.products ?? []).map((p, index) => {
    const key = p.optionName ?? p.productName;
    const cartEntry = cartQueueByOptionName.get(key)?.shift();
    return {
      ...p,
      index,
      count: cartEntry?.count ?? p.productQuantity,
      deliveryMethod: cartEntry?.method,
    };
  });

  const renderProduct = (item: (typeof products)[number]) => (
    <li
      key={item.productName + item.index}
      className={cn(
        "flex justify-between",
        rejected && "opacity-50 line-through",
      )}
    >
      <div>
        <p className="tracking-tight text-sm">
          {item.count}x {item.productName}
        </p>
        {item.optionName && (
          <p className="text-xs text-foreground/50">{item.optionName}</p>
        )}
      </div>
      <p className="tracking-tight text-sm">{item.productPrice}</p>
    </li>
  );

  if (scenario === "mixed") {
    const tableProducts = products.filter(
      (p) => p.deliveryMethod !== "PICKUP_AT_COUNTER",
    );
    const counterProducts = products.filter(
      (p) => p.deliveryMethod === "PICKUP_AT_COUNTER",
    );

    return (
      <ul className="space-y-3">
        {tableProducts.length > 0 && (
          <>
            <li className="text-xs text-foreground/50 uppercase tracking-wide">
              Delivered to your table
            </li>
            {tableProducts.map(renderProduct)}
          </>
        )}
        {counterProducts.length > 0 && (
          <>
            <li className="text-xs text-foreground/50 uppercase tracking-wide mt-2">
              Pickup at kitchen counter
            </li>
            {counterProducts.map(renderProduct)}
          </>
        )}
      </ul>
    );
  }

  return <ul className="space-y-3">{products.map(renderProduct)}</ul>;
}

function ConfirmationMessage({
  order,
  ticketStatus,
}: {
  order: Order;
  ticketStatus: string;
}) {
  const hasCustomOrderNames = order?.hasCustomOrderNames;
  const isTakeAway = order.isTakeAway || false;
  const customerName = order.customerName;

  // Handle different ticket statuses first
  if (ticketStatus === "PENDING") {
    return (
      <div className="mt-2 text-base">
        <p>
          Your order has been received and is awaiting confirmation from the
          kitchen.
        </p>
        <p className="mt-2 flex items-center justify-center gap-1">
          This page will refresh automatically
          <span className="inline-flex">
            <span className="animate-pulse">.</span>
            <span className="animate-pulse" style={{ animationDelay: "0.2s" }}>
              .
            </span>
            <span className="animate-pulse" style={{ animationDelay: "0.4s" }}>
              .
            </span>
          </span>
        </p>
      </div>
    );
  }

  if (ticketStatus === "REJECTED") {
    return (
      <p className="mt-2 text-base">
        Unfortunately, your order has been cancelled by the kitchen. Your
        payment will be refunded automatically within 3-5 business days.
      </p>
    );
  }

  // Delivery method logic for confirmed dine-in orders
  if (!hasCustomOrderNames && !isTakeAway) {
    const scenario = getDeliveryMethodScenario(order);

    if (scenario === "counter") {
      return (
        <p className="mt-2 text-base">
          Your order has been received. All items must be{" "}
          <strong>picked up at the kitchen counter</strong>.{" "}
          {customerName ? (
            <>
              We will call <strong className="whitespace-nowrap">{customerName}</strong> when your order is
              ready.
            </>
          ) : (
            "You will receive an SMS when they are ready."
          )}
        </p>
      );
    }

    if (scenario === "mixed") {
      return (
        <p className="mt-2 text-base">
          Your order has been received.
          <br /> Some items will be{" "}
          <strong>delivered to table {order.table}</strong>, while others must
          be <strong>picked up at the kitchen counter</strong>.{" "}
          {customerName ? (
            <>
              We will call <strong className="whitespace-nowrap">{customerName}</strong> when your counter
              items are ready.
            </>
          ) : (
            "You will receive an SMS when your counter items are ready."
          )}
        </p>
      );
    }
  }

  // Original logic for confirmed orders (IN_PROGRESS/COMPLETED)
  if (hasCustomOrderNames) {
    return (
      <p className="mt-2 text-base">
        The staff will call out your order: "
        <span className="underline">{order.name}</span>" when your order is
        ready.
      </p>
    );
  }
  if (isTakeAway) {
    const pickupTime = order.pickupTime
      ? new Date(order.pickupTime).toLocaleTimeString("da-DK", {
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Europe/Copenhagen",
        })
      : "";

    return (
      <p className="mt-2 text-base">
        Your order has been received and will be ready for pickup{" "}
        {pickupTime ? `at ${pickupTime} (estimated)` : "shortly."}.{" "}
        {customerName && (
          <>
            We will call <strong className="whitespace-nowrap">{customerName}</strong> when it's ready.
          </>
        )}
      </p>
    );
  }

  return (
    <p className="mt-2 text-base">
      Your order has been received and will be with you soon at{" "}
      <strong>table {order.table}</strong>. <br />
      Please do not relocate.{" "}
      {customerName && (
        <>
          We will call <strong className="whitespace-nowrap">{customerName}</strong> when it's ready.
        </>
      )}
    </p>
  );
}
